import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Heading, Text } from "@nbs/ui";
import { DASHBOARD_COCKPIT_QUERY } from "@/features/analytics/api/documents";
import { CoverageMatrix } from "@/features/analytics/components/CoverageMatrix";
import { InsightList } from "@/features/analytics/components/InsightList";
import { downloadCsv } from "@/features/analytics/utils/download-csv";
import { toLandCoverChart } from "@/features/analytics/utils/land-cover";
import {
  filterLandCoverRowsByPublishedYears,
  isKounounkanTerritoryScope,
} from "@/features/story/data/services";
import {
  canStackLandCoverSeries,
  summarizeLandCoverTrend,
} from "@/features/analytics/utils/land-cover-summary";
import { NbsAreaChart } from "@/features/charts/NbsAreaChart";
import { NbsChartContainer } from "@/features/charts/NbsChartContainer";
import { NbsChartDataTable } from "@/features/charts/NbsChartDataTable";
import { NbsChartEmptyState } from "@/features/charts/NbsChartEmptyState";
import { NbsDonutChart } from "@/features/charts/NbsDonutChart";
import { NbsHorizontalBarChart } from "@/features/charts/NbsHorizontalBarChart";
import { NbsKpiCard } from "@/features/charts/NbsKpiCard";
import { NbsRadarChart } from "@/features/charts/NbsRadarChart";
import { NbsGlobalFilters } from "@/features/filters/NbsGlobalFilters";
import { useDashboardFilters } from "@/features/filters/useDashboardFilters";
import { RoleContextBanner } from "@/features/rbac/RoleContextBanner";
import { usePermissions } from "@/hooks/usePermissions";

const DEFAULT_COMPARISON = ["nbs-pay-allassoya-moussaya", "nbs-zon-madina-oula"];
const DEFAULT_RADAR = ["nbs-pay-allassoya-moussaya", "nbs-zon-madina-oula", "nbs-pay-kounounkan"];
const DEFAULT_DONUT_TERRITORY = "nbs-pay-allassoya-moussaya";

export function DashboardCockpit() {
  const permissions = usePermissions();
  const { state, setState, reset, dateFrom, dateTo, activeCount } = useDashboardFilters();

  const comparisonTerritories =
    state.territories.length >= 1 ? state.territories.slice(0, 3) : DEFAULT_COMPARISON;
  const radarTerritories =
    state.radarTerritories.length >= 1 ? state.radarTerritories.slice(0, 3) : DEFAULT_RADAR;
  const donutTerritoryId = state.territories[0] ?? DEFAULT_DONUT_TERRITORY;
  const snapshotYear = state.year || state.toYear;

  const { data, loading, error, refetch } = useQuery(DASHBOARD_COCKPIT_QUERY, {
    variables: {
      territoryIds: state.territories.length ? state.territories : null,
      serviceIds: state.serviceIds.length ? state.serviceIds : null,
      dateFrom,
      dateTo,
      metric: state.metric,
      erosionIndicateurId: "nbs-ind-distribution-erosion",
      vetIndicateurId: "nbs-ind-eco-valeur-totale-vet",
      comparisonIndicateurId: "nbs-ind-superficie-occupation-sol",
      comparisonTerritories,
      donutTerritoryId,
      snapshotYear,
      radarTerritoryIds: radarTerritories,
    },
    fetchPolicy: "cache-and-network",
  });

  const kpis = data?.dashboardKpis;
  const options = data?.availableFilterOptions;
  const landCover = data?.landCoverSeries?.[0];
  const insights = data?.dashboardInsights ?? [];
  const erosion = data?.erosionDistribution;
  const coverage = data?.indicatorCoverage;
  const distribution = data?.landCoverDistribution;
  const profilesResult = data?.territoryProfiles;
  const economicSeries = data?.economicSeries;
  const vetPeriodYear = useMemo((): string | null => {
    const points = (economicSeries?.points ?? []) as Array<{ periodStart?: string | null }>;
    const years = points
      .map((p) => p.periodStart?.slice(0, 4))
      .filter((y): y is string => typeof y === "string" && /^\d{4}$/.test(y));
    if (years.length === 0) return null;
    const unique = [...new Set(years)].sort((a, b) => a.localeCompare(b));
    const first = unique[0] as string;
    const last = unique[unique.length - 1] as string;
    return first === last ? first : `${first}–${last}`;
  }, [economicSeries]);

  const landCoverChart = useMemo(() => {
    const chart = toLandCoverChart(landCover?.points ?? []);
    if (!isKounounkanTerritoryScope(state.territories)) return chart;
    return {
      ...chart,
      byYear: filterLandCoverRowsByPublishedYears(chart.byYear, "kounounkan"),
    };
  }, [landCover, state.territories]);

  const seriesKeys = landCoverChart.classes.slice(0, 8);
  const stackAllowed = canStackLandCoverSeries(state.metric, seriesKeys.length);
  const areaStacked = state.areaMode === "stacked" && stackAllowed;
  const landCoverSummary = useMemo(
    () => summarizeLandCoverTrend(landCoverChart.byYear, seriesKeys),
    [landCoverChart.byYear, seriesKeys],
  );

  const donutSegments = useMemo(
    () =>
      (distribution?.segments ?? []).map((s: { label: string; value: number }) => ({
        name: s.label,
        value: s.value,
      })),
    [distribution],
  );

  const radarReady =
    (profilesResult?.comparableAxisCount ?? 0) >= 3 && (profilesResult?.profiles?.length ?? 0) >= 2;

  const radarSeries = useMemo(
    () =>
      (profilesResult?.profiles ?? []).map(
        (p: { territoryId: string; territoryLabel: string }, i: number) => ({
          id: p.territoryId,
          label: p.territoryLabel,
          color: ["#1f6f4a", "#c47b2c", "#2f5d8c"][i % 3],
        }),
      ),
    [profilesResult],
  );

  const { radarData, radarMeta } = useMemo(() => {
    type RadarMeta = {
      axisId: string;
      seriesId: string;
      normalizedValue: number;
      rawValue: number;
      rawUnit: string;
      normalizationMethod: string;
    };
    const profiles = profilesResult?.profiles ?? [];
    if (!profiles.length) {
      return {
        radarData: [] as Array<Record<string, string | number>>,
        radarMeta: [] as RadarMeta[],
      };
    }
    const axisIds: string[] = profiles[0]?.axes?.map((a: { axisId: string }) => a.axisId) ?? [];
    const radarData = axisIds.map((axisId) => {
      const label =
        profiles[0]?.axes?.find((a: { axisId: string }) => a.axisId === axisId)?.axisLabel ??
        axisId;
      const row: Record<string, string | number> = { axis: label, axisId };
      for (const p of profiles) {
        const ax = p.axes.find((a: { axisId: string }) => a.axisId === axisId);
        row[p.territoryId] = ax?.normalizedValue ?? 0;
      }
      return row;
    });
    const radarMeta = profiles.flatMap(
      (p: {
        territoryId: string;
        axes: Array<{
          axisId: string;
          normalizedValue: number;
          rawValue: number;
          rawUnit: string;
          normalizationMethod: string;
        }>;
      }) =>
        p.axes.map((a) => ({
          axisId: a.axisId,
          seriesId: p.territoryId,
          normalizedValue: a.normalizedValue,
          rawValue: a.rawValue,
          rawUnit: a.rawUnit,
          normalizationMethod: a.normalizationMethod,
        })),
    );
    return { radarData, radarMeta };
  }, [profilesResult]);

  const alerts = insights.filter(
    (i: { severity: string; kind: string }) => i.severity === "critique" || i.kind === "alerte",
  );

  // Real per-interval mini-series derived from data already in the response.
  // `indicatorCoverage.cells` carry periodYear / mesureCount / status, so we
  // can build honest yearly trends for the count/coverage KPIs; the land-cover
  // class series feeds the cultivated / forest cards. No invented values.
  const kpiSparks = useMemo(() => {
    type Cell = {
      periodYear?: number | string | null;
      status?: string | null;
      mesureCount?: number | null;
      indicateurId?: string | null;
      territoryId?: string | null;
    };
    const cells = (coverage?.cells ?? []) as Cell[];
    const years = [
      ...new Set(cells.map((c) => Number(c.periodYear)).filter((y) => Number.isFinite(y))),
    ].sort((a, b) => a - b);
    const per = (fn: (rows: Cell[]) => number) =>
      years.map((y) => fn(cells.filter((c) => Number(c.periodYear) === y)));
    return {
      measures: per((rows) => rows.reduce((s, c) => s + (c.mesureCount ?? 0), 0)),
      indicators: per(
        (rows) =>
          new Set(
            rows
              .filter((c) => c.status === "available" || c.status === "AVAILABLE_IN_REPORT")
              .map((c) => c.indicateurId),
          ).size,
      ),
      territories: per((rows) => new Set(rows.map((c) => c.territoryId)).size),
      coveragePct: per((rows) => {
        const tot = rows.length;
        const ok = rows.filter(
          (c) => c.status === "available" || c.status === "AVAILABLE_IN_REPORT",
        ).length;
        return tot ? Math.round((ok / tot) * 100) : 0;
      }),
    };
  }, [coverage]);

  const classSpark = (re: RegExp): number[] => {
    const key = seriesKeys.find((k) => re.test(k));
    if (!key) return [];
    return landCoverChart.byYear.map((r) => Number(r[key]) || 0);
  };
  const culturesSpark = classSpark(/cultiv|cultur|agri|champ/i);
  const forestSpark = classSpark(/clair|for[êe]t|savane|arbor/i);

  const pctChange = (s: readonly number[]): number | null => {
    if (s.length < 2) return null;
    const a = s[0] ?? 0;
    const b = s[s.length - 1] ?? 0;
    // Flat series (same value every year) → 0 so the card can show “— 0 stable”.
    if (a === b) return 0;
    if (!a) return null;
    return Math.round(((b - a) / Math.abs(a)) * 1000) / 10;
  };

  const pageState = error ? "error" : loading && !data ? "loading" : !kpis ? "empty" : "populated";

  return (
    <article
      className="nbs-dashboard"
      data-testid="dashboard-cockpit"
      data-state={pageState}
      data-role={permissions.productLabel}
    >
      <header className="nbs-dashboard__header">
        <div>
          <p className="nbs-home__eyebrow">Explorer les données</p>
          <Heading level={1}>Tableau de bord</Heading>
          <Text tone="muted">
            Comparez les territoires, filtrez la période, puis lisez les changements d’occupation
            des sols, les perceptions communautaires et la valeur économique.
            {permissions.can("nav.n1") ? (
              <>
                {" "}
                Pour le récit stratégique, ouvrez <Link to="/n1">l’analyse stratégique</Link>.
              </>
            ) : null}
          </Text>
        </div>
      </header>

      <RoleContextBanner />

      <NbsGlobalFilters
        state={state}
        setState={setState}
        reset={reset}
        activeCount={activeCount}
        territories={options?.territories ?? []}
        services={options?.services ?? []}
        indicateurs={options?.indicateurs ?? []}
        years={options?.years ?? [2005, 2015, 2025]}
        showDatePicker={false}
      />

      {error ? (
        <Text role="alert" className="nbs-text--error">
          {error.message}{" "}
          <button type="button" className="nbs-link-button" onClick={() => void refetch()}>
            Réessayer
          </button>
        </Text>
      ) : null}

      <section className="nbs-home__band" aria-labelledby="dash-kpis-heading">
        <div className="nbs-home__band-head">
          <Heading level={2} id="dash-kpis-heading">
            Repères sur la période filtrée
          </Heading>
          <Text tone="muted" className="nbs-home__band-lead">
            D’abord le périmètre et la solidité des données, puis les changements observés et les
            signaux à suivre.
          </Text>
        </div>
        <p className="nbs-dashboard__kpi-group-label">Périmètre &amp; solidité</p>
        <div className="nbs-kpi-grid" aria-label="Périmètre et solidité des données">
          <NbsKpiCard
            label="Mesures publiées / validées"
            icon="measure"
            value={kpis?.validatedMeasureCount}
            spark={kpiSparks.measures}
            delta={pctChange(kpiSparks.measures)}
            deltaLabel="sur la période"
          />
          <NbsKpiCard
            label="Indicateurs renseignés"
            icon="indicator"
            value={kpis?.indicatorWithDataCount}
            unit={`/ couverture ${kpis ? Math.round(kpis.coverageRatio * 100) : "—"} %`}
            spark={kpiSparks.indicators}
            delta={pctChange(kpiSparks.indicators)}
            deltaLabel="sur la période"
          />
          <NbsKpiCard
            label="Territoires"
            icon="territory"
            value={kpis?.territoryCount}
            spark={kpiSparks.territories}
            delta={pctChange(kpiSparks.territories)}
            deltaSuffix=""
          />
          <NbsKpiCard
            label="Couverture des données"
            icon="gauge"
            value={kpis ? Math.round(kpis.coverageRatio * 100) : undefined}
            unit="%"
            spark={kpiSparks.coveragePct}
            delta={pctChange(kpiSparks.coveragePct)}
            deltaSuffix=" pts"
            deltaLabel="sur la période"
            status={kpis ? (kpis.coverageRatio >= 0.7 ? "Bonne couverture" : "À compléter") : null}
            statusTone={kpis ? (kpis.coverageRatio >= 0.7 ? "success" : "warning") : "neutral"}
          />
        </div>
        <p className="nbs-dashboard__kpi-group-label">Changements &amp; signaux</p>
        <div className="nbs-kpi-grid" aria-label="Changements et signaux">
          <NbsKpiCard
            label="Cultures (variation)"
            icon="crop"
            value={kpis?.cultivatedVariationPct}
            unit="%"
            valueTone={kpis ? ((kpis.cultivatedVariationPct ?? 0) >= 0 ? "up" : "down") : null}
            status={kpis ? ((kpis.cultivatedVariationPct ?? 0) >= 0 ? "Hausse" : "Baisse") : null}
            statusTone={
              kpis ? ((kpis.cultivatedVariationPct ?? 0) >= 0 ? "success" : "danger") : "neutral"
            }
            spark={culturesSpark}
            n2Href="/n2?indicateurId=nbs-ind-variation-occupation-sol"
          />
          <NbsKpiCard
            label="Forêts claires"
            icon="forest"
            value={kpis?.forestClearVariationPct}
            unit="%"
            valueTone={kpis ? ((kpis.forestClearVariationPct ?? 0) >= 0 ? "up" : "down") : null}
            status={kpis ? ((kpis.forestClearVariationPct ?? 0) < 0 ? "Alerte" : "Stable") : null}
            statusTone={
              kpis ? ((kpis.forestClearVariationPct ?? 0) < 0 ? "danger" : "neutral") : "neutral"
            }
            spark={forestSpark}
            n2Href="/n2?indicateurId=nbs-ind-variation-occupation-sol"
          />
          <NbsKpiCard
            label="Érosion (perception)"
            icon="erosion"
            value={kpis?.erosionPerceptionPct}
            unit="%"
            n2Href="/n2?indicateurId=nbs-ind-distribution-erosion"
          />
          <NbsKpiCard
            label="Valeur économique (VET)"
            icon="coin"
            value={kpis?.economicTotalVet}
            unit={kpis?.economicUnit}
            period={vetPeriodYear}
            n2Href="/n2?indicateurId=nbs-ind-eco-valeur-totale-vet"
          />
          <NbsKpiCard
            label="Alertes"
            icon="alert"
            value={kpis?.alertCount ?? alerts.length}
            status={(kpis?.alertCount ?? alerts.length) > 0 ? "À traiter" : "Aucune"}
            statusTone={(kpis?.alertCount ?? alerts.length) > 0 ? "danger" : "success"}
          />
        </div>
      </section>

      <section className="nbs-home__band" aria-labelledby="dash-land-heading">
        <div className="nbs-home__band-head">
          <Heading level={2} id="dash-land-heading">
            Que s’est-il passé sur les sols ?
          </Heading>
          <Text tone="muted" className="nbs-home__band-lead">
            {landCoverSummary ||
              "Évolution et répartition de l’occupation des sols pour la période et les territoires filtrés."}
          </Text>
        </div>
        <div className="nbs-dashboard__grid-2">
          <NbsChartContainer
            title="Évolution de l’occupation des sols"
            description="Surfaces par classe d’occupation du sol — ce qui s’étend, ce qui recule."
            unit={landCover?.unit}
            period={`${state.fromYear}–${state.toYear}`}
            source="Tableaux 4.1–4.3 / Figures 4.12–4.14"
            updatedAt={kpis?.latestYear ? String(kpis.latestYear) : null}
            isLoading={loading && !landCover}
            empty={!loading && landCoverChart.byYear.length === 0}
            emptyMessage="Aucune série pour cette période / ce territoire."
            actions={
              <div className="nbs-chart-actions">
                <label className="nbs-chart-mode">
                  Affichage
                  <select
                    aria-label="Mode d’affichage du graphique"
                    value={areaStacked ? "stacked" : "overlay"}
                    onChange={(e) => {
                      const next = e.target.value === "stacked" ? "stacked" : "overlay";
                      if (next === "stacked" && !stackAllowed) return;
                      setState({ areaMode: next });
                    }}
                  >
                    <option value="overlay">Superposé</option>
                    <option value="stacked" disabled={!stackAllowed}>
                      Empilé{!stackAllowed ? " (parts % uniquement)" : ""}
                    </option>
                  </select>
                </label>
                {landCoverChart.byYear.length ? (
                  <button
                    type="button"
                    className="nbs-link-button"
                    onClick={() =>
                      downloadCsv("occupation-sols.csv", [
                        ["année", ...seriesKeys],
                        ...landCoverChart.byYear.map((r) => [
                          String(r.year),
                          ...seriesKeys.map((c) => String(r[c] ?? "")),
                        ]),
                      ])
                    }
                  >
                    Exporter CSV
                  </button>
                ) : null}
              </div>
            }
            table={
              landCoverChart.byYear.length === 0 && !loading ? (
                <NbsChartEmptyState onResetFilters={reset} />
              ) : landCoverChart.points[0] ? (
                <>
                  <p className="nbs-chart-summary" data-testid="land-cover-summary">
                    {landCoverSummary}
                  </p>
                  <NbsChartDataTable
                    caption="Tableau alternatif — occupation des sols"
                    headers={["Année", ...seriesKeys.slice(0, 6)]}
                    rows={landCoverChart.byYear.map((r) => [
                      String(r.year),
                      ...seriesKeys.slice(0, 6).map((c) => String(r[c] ?? "—")),
                    ])}
                  />
                  <p className="nbs-chart-container__alt">
                    <Link
                      to={`/n2?indicateurId=${landCover?.indicateurId ?? "nbs-ind-superficie-occupation-sol"}`}
                    >
                      Voir le détail de l’indicateur
                    </Link>
                    {" · "}
                    <Link to={`/n3?mesureIds=${landCoverChart.points[0].mesureId}`}>
                      Voir une mesure associée
                    </Link>
                  </p>
                </>
              ) : null
            }
          >
            <NbsAreaChart
              data={landCoverChart.byYear}
              xKey="year"
              seriesKeys={seriesKeys}
              stacked={areaStacked}
              unit={landCover?.unit}
            />
          </NbsChartContainer>

          <NbsChartContainer
            title="Répartition de l’occupation des sols"
            description="Part de chaque classe d’occupation pour l’année choisie — où se concentre le paysage aujourd’hui."
            unit={distribution?.unit ?? "%"}
            period={distribution?.year ?? snapshotYear}
            territory={distribution?.territoryLabel}
            source="nbs-ind-part-occupation-sol"
            isLoading={loading && !distribution}
            empty={!loading && donutSegments.length === 0}
            emptyMessage="Aucune répartition pour ce territoire / cette année."
            actions={
              <label className="nbs-chart-mode">
                Année
                <select
                  aria-label="Année du donut"
                  value={snapshotYear}
                  onChange={(e) => setState({ year: e.target.value })}
                >
                  {(options?.years ?? [2005, 2015, 2025]).map((y: number) => (
                    <option key={y} value={String(y)}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>
            }
            table={
              donutSegments.length ? (
                <>
                  {distribution?.anomaly ? (
                    <Text role="status" className="nbs-text--warning">
                      {distribution.anomaly}
                    </Text>
                  ) : null}
                  <NbsChartDataTable
                    caption="Tableau alternatif — segments du donut"
                    headers={["Classe", "Valeur", "Unité"]}
                    rows={(distribution?.detailSegments ?? distribution?.segments ?? []).map(
                      (s: { label: string; value: number; unit: string }) => [
                        s.label,
                        String(s.value),
                        s.unit,
                      ],
                    )}
                  />
                </>
              ) : null
            }
          >
            <NbsDonutChart
              data={donutSegments}
              centerLabel={distribution?.year ?? snapshotYear}
              centerValue={
                distribution?.total != null
                  ? new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(
                      distribution.total,
                    )
                  : null
              }
              centerUnit="%"
            />
          </NbsChartContainer>
        </div>
      </section>

      <section className="nbs-home__band" aria-labelledby="dash-compare-heading">
        <div className="nbs-home__band-head">
          <Heading level={2} id="dash-compare-heading">
            Où les territoires se ressemblent — et où ils divergent
          </Heading>
          <Text tone="muted" className="nbs-home__band-lead">
            Comparaison normalisée (0–100) pour lire rapidement les contrastes entre paysages.
          </Text>
        </div>
        <NbsChartContainer
          title="Profil comparatif des territoires"
          description="Indices normalisés — utile pour prioriser où regarder, pas pour remplacer les valeurs brutes."
          period={profilesResult?.year ?? snapshotYear}
          source="occupation des sols + perceptions"
          isLoading={loading && !profilesResult}
          empty={!loading && !radarReady}
          emptyMessage="Les données disponibles ne permettent pas encore une comparaison multidimensionnelle fiable."
          actions={
            <label className="nbs-chart-mode">
              Territoires radar (max 3)
              <select
                aria-label="Territoires radar"
                multiple
                value={radarTerritories}
                onChange={(e) =>
                  setState({
                    radarTerritories: Array.from(e.target.selectedOptions)
                      .map((o) => o.value)
                      .slice(0, 3),
                  })
                }
              >
                {(options?.territories ?? []).map((t: { id: string; label: string }) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
          }
          table={
            radarReady ? (
              <NbsChartDataTable
                caption="Tableau alternatif — axes normalisés"
                headers={[
                  "Axe",
                  ...radarSeries.map((s: { label: string }) => s.label),
                  "Unité brute",
                ]}
                rows={radarData.map((row) => {
                  const axisId = String(row.axisId);
                  const unit =
                    radarMeta.find((m: { axisId: string; rawUnit: string }) => m.axisId === axisId)
                      ?.rawUnit ?? "%";
                  return [
                    String(row.axis),
                    ...radarSeries.map((s: { id: string }) => String(row[s.id] ?? "—")),
                    unit,
                  ];
                })}
              />
            ) : null
          }
        >
          {radarReady ? (
            <NbsRadarChart data={radarData as never} series={radarSeries} tooltipMeta={radarMeta} />
          ) : null}
        </NbsChartContainer>
      </section>

      <section className="nbs-home__band" aria-labelledby="dash-people-heading">
        <div className="nbs-home__band-head">
          <Heading level={2} id="dash-people-heading">
            Que disent les communautés — et quelle valeur économique ?
          </Heading>
          <Text tone="muted" className="nbs-home__band-lead">
            Perceptions locales et estimation de la valeur économique totale (VET) des services.
          </Text>
        </div>
        <div className="nbs-n1-analytics__split">
          <NbsChartContainer
            title="Perception communautaire de l’érosion"
            description={erosion?.libelle}
            unit="%"
            territory={erosion?.territoryLabel}
            source={`Enquête UICN/ELA · n=${erosion?.sampleSize ?? "—"}`}
            isLoading={loading && !erosion}
            empty={!loading && !erosion?.buckets?.length}
          >
            <NbsHorizontalBarChart
              data={(erosion?.buckets ?? []).map((b: { modalityLabel: string; value: number }) => ({
                label: b.modalityLabel,
                value: b.value,
              }))}
            />
          </NbsChartContainer>

          <NbsChartContainer
            title="Valeur économique (VET)"
            description="Ordre de grandeur de la valeur économique totale des services écosystémiques."
            unit={data?.economicSeries?.unit}
            period={vetPeriodYear}
            source="Tableau 4.14"
            empty={!loading && !data?.economicSeries?.points?.length}
          >
            <NbsHorizontalBarChart
              data={
                data?.economicSeries?.points?.[0]
                  ? [
                      {
                        label: data.economicSeries.libelle,
                        value: data.economicSeries.points[0].value,
                      },
                    ]
                  : []
              }
              unit="$ US/ha/an"
              color="#c47b2c"
            />
          </NbsChartContainer>
        </div>
      </section>

      <section className="nbs-home__band" aria-labelledby="dash-follow-heading">
        <div className="nbs-home__band-head">
          <Heading level={2} id="dash-follow-heading">
            Que faut-il encore renseigner pour bien suivre ?
          </Heading>
          <Text tone="muted" className="nbs-home__band-lead">
            Matrice de couverture et insights — pour passer de l’état des lieux au suivi dans le
            temps.
          </Text>
        </div>
        <CoverageMatrix
          cells={coverage?.cells ?? []}
          completenessRatio={coverage?.completenessRatio}
          isLoading={loading}
          onResetFilters={reset}
          preferredYear={snapshotYear}
        />

        <InsightList title="Insights et alertes" insights={insights} loading={loading} />
      </section>
    </article>
  );
}
